import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Grid,
  Divider,
  Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import emailjs from '@emailjs/browser';

const steps = ['Personal Information', 'Experience & Business', 'References & Legal'];

const TrainerApplicationModal = ({ open, onClose }) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Section 1: Personal Information
    fullName: '',
    preferredName: '',
    pronouns: '',
    phone: '',
    email: '',
    socialMedia: '',
    mailingAddress: '',
    dateOfBirth: '',

    // Section 2: Certifications & Education
    primaryCertification: '',
    additionalCertifications: '',
    cprCertification: '',

    // Section 3: Experience & Specialties
    yearsExperience: '',
    clientTypes: [],
    trainingModalities: [],
    previousGyms: '',

    // Section 4: Availability & Commitment
    preferredSchedule: '',
    hoursPerWeek: '',
    rentalOption: '',
    startDate: '',
    schedulingNotes: '',

    // Section 5: Business & Operations
    existingClients: '',
    numberOfClients: '',
    liabilityInsurance: '',
    businessEntity: '',
    exerciseComComfort: '',
    marketingOptIn: '',

    // Section 6: Professional References
    reference1Name: '',
    reference1Relationship: '',
    reference1Phone: '',
    reference1Email: '',
    reference2Name: '',
    reference2Relationship: '',
    reference2Phone: '',
    reference2Email: '',

    // Section 7: Legal & Acknowledgement
    signature: '',
    signatureDate: ''
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleCheckboxChange = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData.fullName) newErrors.fullName = 'Full name is required';
      if (!formData.phone) newErrors.phone = 'Phone number is required';
      if (!formData.email) newErrors.email = 'Email is required';
      if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
      if (!formData.primaryCertification) newErrors.primaryCertification = 'Primary certification is required';
      if (!formData.yearsExperience) newErrors.yearsExperience = 'Years of experience is required';
      if (formData.clientTypes.length === 0) newErrors.clientTypes = 'At least one client type is required';
      if (formData.trainingModalities.length === 0) newErrors.trainingModalities = 'At least one training modality is required';
    } else if (step === 1) {
      if (!formData.preferredSchedule) newErrors.preferredSchedule = 'Preferred schedule is required';
      if (!formData.hoursPerWeek) newErrors.hoursPerWeek = 'Hours per week is required';
      if (!formData.existingClients) newErrors.existingClients = 'Please indicate if you have existing clients';
      if (!formData.businessEntity) newErrors.businessEntity = 'Business entity is required';
      if (!formData.exerciseComComfort) newErrors.exerciseComComfort = 'Comfort level with Exercise.com is required';
    } else if (step === 2) {
      if (!formData.reference1Name) newErrors.reference1Name = 'Reference 1 name is required';
      if (!formData.reference1Relationship) newErrors.reference1Relationship = 'Reference 1 relationship is required';
      if (!formData.reference1Phone) newErrors.reference1Phone = 'Reference 1 phone is required';
      if (!formData.reference1Email) newErrors.reference1Email = 'Reference 1 email is required';
      if (!formData.signature) newErrors.signature = 'Signature is required';
      if (!formData.signatureDate) newErrors.signatureDate = 'Signature date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;

    setSubmitting(true);
    try {
      // EmailJS service configuration
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      // Check if credentials are loaded
      if (!serviceId || !templateId || !publicKey) {
        alert('EmailJS configuration is missing. Please check your .env file.');
        setSubmitting(false);
        return;
      }

      // Prepare email content
      const emailContent = Object.entries(formData)
        .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
        .join('\n');

      await emailjs.send(
        serviceId,
        templateId,
        {
          from_name: formData.fullName,
          from_email: formData.email,
          message: emailContent,
              to_email: 'Varsityfitnesslb@outlook.com'
        },
        publicKey
      );

      alert('Application submitted successfully!');
      onClose();
      setFormData({}); // Reset form
      setActiveStep(0);
    } catch (error) {
      alert('Failed to submit application. Please try again.');
      console.error('EmailJS error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            {/* Section 1 */}
            <Typography variant="h6" sx={{ mb: 3, color: theme.palette.primary.main }}>
              Section 1 — Personal Information
            </Typography>

            {/* Row 1: Name fields */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Full Legal Name"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  error={!!errors.fullName}
                  helperText={errors.fullName}
                  sx={{ minWidth: 400 }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Preferred Name"
                  value={formData.preferredName}
                  onChange={(e) => handleInputChange('preferredName', e.target.value)}
                  sx={{ minWidth: 400 }}
                />
              </Grid>
            </Grid>

            {/* Row 2: Pronouns and Phone */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ minWidth: 200 }}>
                  <InputLabel >Pronouns</InputLabel>
                  <Select
                    value={formData.pronouns}
                    onChange={(e) => handleInputChange('pronouns', e.target.value)}
                  >
                    <MenuItem value="He/Him">He/Him</MenuItem>
                    <MenuItem value="She/Her">She/Her</MenuItem>
                    <MenuItem value="They/Them">They/Them</MenuItem>
                    <MenuItem value="Prefer Not to Say">Prefer Not to Say</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="Phone Number"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  required
                />
              </Grid>
            </Grid>

            {/* Row 3: Email and Social Media */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="Social Media Handles"
                  value={formData.socialMedia}
                  onChange={(e) => handleInputChange('socialMedia', e.target.value)}
                />
              </Grid>
            </Grid>

            {/* Row 4: Mailing Address (full width) */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Mailing Address"
                  value={formData.mailingAddress}
                  onChange={(e) => handleInputChange('mailingAddress', e.target.value)}
                />
              </Grid>
            </Grid>

            {/* Row 5: Date of Birth */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  error={!!errors.dateOfBirth}
                  helperText={errors.dateOfBirth}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Section 2 */}
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
              Section 2 — Certifications & Education
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.primaryCertification} sx={{ minWidth: 300 }}>
                  <InputLabel>Personal Training Certification *</InputLabel>
                  <Select
                    value={formData.primaryCertification}
                    onChange={(e) => handleInputChange('primaryCertification', e.target.value)}
                  >
                    <MenuItem value="NASM">NASM</MenuItem>
                    <MenuItem value="ACE">ACE</MenuItem>
                    <MenuItem value="ISSA">ISSA</MenuItem>
                    <MenuItem value="NSCA">NSCA</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                  {errors.primaryCertification && <Typography variant="caption" color="error">{errors.primaryCertification}</Typography>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="Additional Certifications"
                  value={formData.additionalCertifications}
                  onChange={(e) => handleInputChange('additionalCertifications', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="CPR/AED Certification – Provider & Expiration Date"
                  value={formData.cprCertification}
                  onChange={(e) => handleInputChange('cprCertification', e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Section 3 */}
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
              Section 3 — Experience & Specialties
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.yearsExperience}  sx={{ minWidth: 400 }}>
                  <InputLabel>Years of Personal Training Experience *</InputLabel>
                  <Select
                    value={formData.yearsExperience}
                    onChange={(e) => handleInputChange('yearsExperience', e.target.value)}
                  >
                    <MenuItem value="0–1">0–1</MenuItem>
                    <MenuItem value="1–3">1–3</MenuItem>
                    <MenuItem value="3–5">3–5</MenuItem>
                    <MenuItem value="5+">5+</MenuItem>
                  </Select>
                  {errors.yearsExperience && <Typography variant="caption" color="error">{errors.yearsExperience}</Typography>}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Types of Clients You've Worked With *</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {['Beginners', 'Athletes', 'Seniors', 'Rehab', 'Weight Loss', 'General Population', 'Other'].map(type => (
                    <FormControlLabel
                      key={type}
                      control={
                        <Checkbox
                          checked={formData.clientTypes.includes(type)}
                          onChange={(e) => handleCheckboxChange('clientTypes', type, e.target.checked)}
                        />
                      }
                      label={type}
                    />
                  ))}
                </Box>
                {errors.clientTypes && <Typography variant="caption" color="error">{errors.clientTypes}</Typography>}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Training Modalities / Specialties *</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {['Strength Training', 'HIIT', 'Mobility', 'Sports Performance', 'Corrective Exercise', 'Bodybuilding', 'Weight Loss', 'Other'].map(modality => (
                    <FormControlLabel
                      key={modality}
                      control={
                        <Checkbox
                          checked={formData.trainingModalities.includes(modality)}
                          onChange={(e) => handleCheckboxChange('trainingModalities', modality, e.target.checked)}
                        />
                      }
                      label={modality}
                    />
                  ))}
                </Box>
                {errors.trainingModalities && <Typography variant="caption" color="error">{errors.trainingModalities}</Typography>}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="Previous Gyms, Studios, or Facilities"
                  value={formData.previousGyms}
                  onChange={(e) => handleInputChange('previousGyms', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box>
            {/* Section 4 */}
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
              Section 4 — Availability & Commitment
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Preferred Training Schedule – Days & Times"
                  multiline
                  rows={3}
                  value={formData.preferredSchedule}
                  onChange={(e) => handleInputChange('preferredSchedule', e.target.value)}
                  error={!!errors.preferredSchedule}
                  helperText={errors.preferredSchedule}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.hoursPerWeek}>
                  <InputLabel>Approximate Hours or Clients Per Week *</InputLabel>
                  <Select
                    value={formData.hoursPerWeek}
                    onChange={(e) => handleInputChange('hoursPerWeek', e.target.value)}
                  >
                    <MenuItem value="5–10">5–10</MenuItem>
                    <MenuItem value="10–20">10–20</MenuItem>
                    <MenuItem value="20–30">20–30</MenuItem>
                    <MenuItem value="30+">30+</MenuItem>
                  </Select>
                  {errors.hoursPerWeek && <Typography variant="caption" color="error">{errors.hoursPerWeek}</Typography>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Rental Option Interested In</InputLabel>
                  <Select
                    value={formData.rentalOption}
                    onChange={(e) => handleInputChange('rentalOption', e.target.value)}
                  >
                    <MenuItem value="Hourly">Hourly</MenuItem>
                    <MenuItem value="Monthly Unlimited">Monthly Unlimited</MenuItem>
                    <MenuItem value="Undecided">Undecided</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Expected Start Date"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="Additional Scheduling Notes"
                  multiline
                  rows={3}
                  value={formData.schedulingNotes}
                  onChange={(e) => handleInputChange('schedulingNotes', e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Section 5 */}
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
              Section 5 — Business & Operations
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl component="fieldset" error={!!errors.existingClients}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Do You Currently Have Existing Clients? *</Typography>
                  <RadioGroup
                    value={formData.existingClients}
                    onChange={(e) => handleInputChange('existingClients', e.target.value)}
                  >
                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="No" control={<Radio />} label="No" />
                  </RadioGroup>
                  {errors.existingClients && <Typography variant="caption" color="error">{errors.existingClients}</Typography>}
                </FormControl>
              </Grid>
              {formData.existingClients === 'Yes' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    sx={{ minWidth: 280 }}
                    label="If Yes, Approximately How Many Will You Bring?"
                    type="number"
                    value={formData.numberOfClients}
                    onChange={(e) => handleInputChange('numberOfClients', e.target.value)}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="Liability Insurance – Provider & Expiration Date"
                  value={formData.liabilityInsurance}
                  onChange={(e) => handleInputChange('liabilityInsurance', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.businessEntity}>
                  <InputLabel>Business Entity *</InputLabel>
                  <Select
                    value={formData.businessEntity}
                    onChange={(e) => handleInputChange('businessEntity', e.target.value)}
                  >
                    <MenuItem value="LLC">LLC</MenuItem>
                    <MenuItem value="Sole Proprietor">Sole Proprietor</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                  {errors.businessEntity && <Typography variant="caption" color="error">{errors.businessEntity}</Typography>}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset" error={!!errors.exerciseComComfort}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Comfort Level Using Exercise.com *</Typography>
                  <RadioGroup
                    value={formData.exerciseComComfort}
                    onChange={(e) => handleInputChange('exerciseComComfort', e.target.value)}
                  >
                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="No" control={<Radio />} label="No" />
                  </RadioGroup>
                  {errors.exerciseComComfort && <Typography variant="caption" color="error">{errors.exerciseComComfort}</Typography>}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Marketing & Social Media Features Opt-In</Typography>
                  <RadioGroup
                    value={formData.marketingOptIn}
                    onChange={(e) => handleInputChange('marketingOptIn', e.target.value)}
                  >
                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="No" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );
      case 2:
        return (
          <Box>
            {/* Section 6 */}
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
              Section 6 — Professional References
            </Typography>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Reference 1</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="Name"
                  value={formData.reference1Name}
                  onChange={(e) => handleInputChange('reference1Name', e.target.value)}
                  error={!!errors.reference1Name}
                  helperText={errors.reference1Name}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="Relationship"
                  value={formData.reference1Relationship}
                  onChange={(e) => handleInputChange('reference1Relationship', e.target.value)}
                  error={!!errors.reference1Relationship}
                  helperText={errors.reference1Relationship}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="Phone Number"
                  type="tel"
                  value={formData.reference1Phone}
                  onChange={(e) => handleInputChange('reference1Phone', e.target.value)}
                  error={!!errors.reference1Phone}
                  helperText={errors.reference1Phone}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="Email Address"
                  type="email"
                  value={formData.reference1Email}
                  onChange={(e) => handleInputChange('reference1Email', e.target.value)}
                  error={!!errors.reference1Email}
                  helperText={errors.reference1Email}
                  required
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ mb: 2, mt: 3 }}>Reference 2</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="Name"
                  value={formData.reference2Name}
                  onChange={(e) => handleInputChange('reference2Name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="Relationship"
                  value={formData.reference2Relationship}
                  onChange={(e) => handleInputChange('reference2Relationship', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="Phone Number"
                  type="tel"
                  value={formData.reference2Phone}
                  onChange={(e) => handleInputChange('reference2Phone', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="Email Address"
                  type="email"
                  value={formData.reference2Email}
                  onChange={(e) => handleInputChange('reference2Email', e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Section 7 */}
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
              Section 7 — Legal & Acknowledgement
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              I confirm that all information provided in this application is true and accurate to the best of my knowledge.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  sx={{ minWidth: 280 }}
                  label="Electronic Signature – Full Name"
                  value={formData.signature}
                  onChange={(e) => handleInputChange('signature', e.target.value)}
                  error={!!errors.signature}
                  helperText={errors.signature}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  value={formData.signatureDate}
                  onChange={(e) => handleInputChange('signatureDate', e.target.value)}
                  error={!!errors.signatureDate}
                  helperText={errors.signatureDate}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </Grid>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ bgcolor: theme.palette.primary.main, color: 'white' }}>
        Varsity Fitness – New Trainer Application
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {renderStepContent(activeStep)}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button disabled={activeStep === 0} onClick={handleBack}>
          Back
        </Button>
        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Application'}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleNext}>
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TrainerApplicationModal;
